import { Injectable } from '@angular/core';

import { take } from 'rxjs/operators';

import { AppConstants} from '@unpispas/upp-defs';
import { httpService } from './http';
import { languageService } from './language';

/**
 * Enum representing the source of the upload.
 */
export enum uploadSource {
    /** Attachment upload source. */
    attach = 'attach'
}

/**
 * Service for handling file uploads to a remote server.
 */
@Injectable({
    providedIn: 'root'
})
export class uploadService {
    private uploadUrl = AppConstants.baseURL + 'common/upload';
    
    /**
     * Creates an instance of uploadService.
     * @param http - The HTTP service for making requests.
     * @param lang - The language service for handling translations.
     */    
    constructor(private http: httpService, private lang: languageService){
        // nothing to do
    }
    
    /**
     * Uploads a file to the server.
     * @param data - The data to upload, typically file data.
     * @param source - The source of the upload, defaults to `null`.
     * @returns A Promise that resolves with the uploaded file name or rejects with an error message.
     */    
    post(data: any, source: uploadSource | null = null){
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            
            for (const field in data) {
                if (Object.prototype.hasOwnProperty.call(data, field)){
                    formData.append(field, data[field], data[field].name); 
                }
            }
                
            const _getparams = (source) ? '?source=' + source : '';            
            const _request = this.http.post(this.uploadUrl + _getparams, formData, httpService.headers, 'json', 'multipart')
            _request.pipe(take(1)).subscribe({
                next: (data: any) => {
                    if (data['errorcode'] == 0){
                        resolve(data['filename']);
                    }
                    else {
                        reject(this.lang.tr('@service_request_error_' + data['errorcode']));
                    }    
                },
                error: (error) => {
                    reject(this.lang.tr('@http_request_error', [ error.status ]));
                },
                complete: () => {
                    // nothing to do
                }
            });
        });
    };
}

/**
 * Service for handling file downloads from a remote server.
 */
@Injectable({
    providedIn: 'root'
})
export class downloadService {
    private _doDownloadUrl = 'common/download';
    
    /**
     * Creates an instance of downloadService.
     * @param http - The HTTP service for making requests.
     * @param lang - The language service for handling translations.
     */    
    constructor(private http: httpService, private lang: languageService){
        // nothing to do
    }
    
    /**
     * Downloads a file from the server.
     * @param url - The URL of the file to download.
     * @returns A Promise that resolves with the file's binary data as an ArrayBuffer or rejects with an error message.
     */    
    file(url: string){
        return new Promise<ArrayBuffer>((resolve, reject) => {
            const _request = this.http.get(AppConstants.baseURL + this._doDownloadUrl + "?url=" + encodeURIComponent(url));
            _request.pipe(take(1)).subscribe({
                next: (data: any) => {
                    if (data['errorcode'] == 0){
                        const _binary = atob(data['base64']);
                        const _length = _binary.length;                
                        const _bytes = new Uint8Array(_length);
                        for (let i = 0; i < _length; i++) {
                            _bytes[i] = _binary.charCodeAt(i);
                        }
                        resolve(_bytes.buffer);
                    }
                    else {
                        reject(this.lang.tr('@service_request_error_' + data['errorcode']));
                    }    
                },
                error: (error) => {
                    reject(this.lang.tr('@http_request_error', [ error.status ]));
                },
                complete: () => {
                    // nothing to do
                }
            });
        });
    }
}
